/**
 * @file BillingContractsTab.js
 * @description Tab component for managing a client's billing contracts and their signature status.
 */

import { useState, useEffect, useCallback, useContext } from 'react';
import { format, parseISO } from 'date-fns';
import Toast from '../../ui/toasts/Toast';
import Spinner from '../../ui/spinners/Spinner';
import NotFoundBox from '../../ui/no_data_indicators/NotFoundBox';
import styles from '../Billing.module.scss';
import {
    getContracts,
    createContract,
    deleteContract,
} from '../../../services/clientService';
import { fetchContractStatuses } from '../../../services/systemService';
import { uploadFile } from '../../../services/themeService';
import { Dropdown } from '../../helpers/Dropdown';
import CustomDatePicker from '../../helpers/CustomDatePicker_V2';
import { ModalContext, ModalTypes } from '../../../store/ModalContext';
import { getText } from '../../../i18n';
import { ReactComponent as ViewIcon } from '../../../utils/icons/Icon_View.svg';

const getInitialNewContractForm = () => ({
    title: '',
    contractStatusId: null,
    startDate: null,
    completeDate: null,
    fileName: null,
});

const formatDisplayDate = dateString => {
    if (!dateString) return '-';
    try {
        return format(parseISO(dateString), 'MMM dd yyyy');
    } catch (error) {
        return dateString;
    }
};

const CONTRACT_STATUS_BADGE_CLASSES = {
    Negotiation: 'ing-badge-pending',
    'Client Signature': 'ing-badge-template',
    'Internal Signature': 'ing-badge-inv-sent',
    Completed: 'ing-badge-active',
};

const BillingContractsTab = ({ clientId }) => {
    const { showModal } = useContext(ModalContext);
    const [contracts, setContracts] = useState([]);
    const [contractStatuses, setContractStatuses] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [newContractForm, setNewContractForm] = useState(
        getInitialNewContractForm(),
    );
    const [isSaving, setIsSaving] = useState(false);
    const [isUploadingDocument, setIsUploadingDocument] = useState(false);

    const loadContractStatuses = useCallback(async () => {
        try {
            const statuses = await fetchContractStatuses();
            setContractStatuses(Array.isArray(statuses) ? statuses : []);
        } catch (error) {
            console.error('Failed to fetch contract statuses', error);
        }
    }, []);

    const loadContracts = useCallback(async () => {
        if (!clientId) return;

        try {
            setIsLoading(true);
            const response = await getContracts(clientId);
            setContracts(Array.isArray(response) ? response : []);
        } catch (error) {
            console.error('Failed to fetch contracts', error);
            Toast.error(getText('msg_2dc3b5634c63'));
        } finally {
            setIsLoading(false);
        }
    }, [clientId]);

    useEffect(() => {
        loadContractStatuses();
    }, [loadContractStatuses]);

    useEffect(() => {
        loadContracts();
    }, [loadContracts]);

    const getStatusLabel = statusId =>
        contractStatuses.find(status => status.id === statusId)?.name || '-';

    const getStatusBadgeClass = statusId =>
        CONTRACT_STATUS_BADGE_CLASSES[getStatusLabel(statusId)] ||
        'ing-badge-inactive';

    const handleView = contract => {
        if (!contract.fileName) {
            Toast.error(getText('msg_27f36a8639ea'));
            return;
        }

        const fileType = contract.fileName.split('?')[0].split('.').pop();

        showModal({
            type: ModalTypes.ASSET_VIEW,
            title: contract.title,
            text: contract.fileName,
            fileType,
        });
    };

    const handleDelete = async contractId => {
        try {
            await deleteContract(clientId, contractId);
            Toast.success(getText('msg_c42c701ca3bd'));
            loadContracts();
        } catch (error) {
            console.error('Failed to delete contract', error);
            Toast.error(getText('msg_ee7aca7c29b6'));
        }
    };

    const openAddModal = () => {
        setNewContractForm(getInitialNewContractForm());
        setIsAddModalOpen(true);
    };

    const closeAddModal = () => {
        setIsAddModalOpen(false);
    };

    const handleAddContract = async () => {
        if (!newContractForm.title || !newContractForm.contractStatusId) {
            Toast.error(getText('msg_0900cde77762'));
            return;
        }

        try {
            setIsSaving(true);
            await createContract(
                {
                    id: 0,
                    tenantId: clientId,
                    title: newContractForm.title,
                    contractStatusId: newContractForm.contractStatusId,
                    startDate: newContractForm.startDate,
                    fileName: newContractForm.fileName,
                    completeDate: newContractForm.completeDate,
                },
                clientId,
            );
            Toast.success(getText('msg_3ba892eb3866'));
            closeAddModal();
            loadContracts();
        } catch (error) {
            console.error('Failed to add contract', error);
            const errorMessage =
                error.response?.data?.detail || getText('msg_fb137c8303a4');
            Toast.error(errorMessage);
        } finally {
            setIsSaving(false);
        }
    };

    const statusOptions = contractStatuses.map(status => ({
        value: status.id,
        label: status.name,
    }));

    const handleContractFileChange = async e => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            setIsUploadingDocument(true);
            const uploadedFileLink = await uploadFile(file);
            setNewContractForm(prevData => ({
                ...prevData,
                fileName: uploadedFileLink,
            }));
        } catch (error) {
            console.error('Failed to upload contract document', error);
            Toast.error(getText('msg_3a01d1641e1c'));
        } finally {
            setIsUploadingDocument(false);
        }
    };

    if (isLoading) {
        return <Spinner width={50} className={styles.loadingSpinner} />;
    }

    return (
        <div className="col-12">
            <div className="table-responsive-xxl w-100">
                <table className="table table-hover align-middle">
                    <thead className="custom-table-header table-light">
                        <tr>
                            <th className="ps-4">
                                {getText('msg_768e0c1c6957')}
                            </th>
                            <th>{getText('msg_bae7d5be7082')}</th>
                            <th>{getText('msg_faa9e7e7ef5a')}</th>
                            <th>{getText('msg_1798b3ba42ee')}</th>
                            <th>{getText('msg_69bd4ef9fbd0')}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {contracts.map(contract => (
                            <tr key={contract.id}>
                                <td className="ps-4 item-page-changed-by">
                                    {contract.title}
                                </td>
                                <td>
                                    <button
                                        className={getStatusBadgeClass(
                                            contract.contractStatusId,
                                        )}
                                    >
                                        {getStatusLabel(
                                            contract.contractStatusId,
                                        )}
                                    </button>
                                </td>
                                <td className="item-page-changed-by">
                                    {formatDisplayDate(contract.startDate)}
                                </td>
                                <td className="item-page-changed-by">
                                    {formatDisplayDate(contract.completeDate)}
                                </td>
                                <td>
                                    <span
                                        type="button"
                                        role="button"
                                        tabIndex={0}
                                        title={getText('msg_69bd4ef9fbd0')}
                                        className={styles.iconButton}
                                        onClick={() => handleView(contract)}
                                        onKeyDown={event => {
                                            if (
                                                event.key === 'Enter' ||
                                                event.key === ' '
                                            ) {
                                                handleView(contract);
                                            }
                                        }}
                                    >
                                        <ViewIcon />
                                    </span>
                                </td>
                                <td>
                                    <span
                                        type="button"
                                        onClick={() =>
                                            handleDelete(contract.id)
                                        }
                                        className="ing-text-buttons"
                                    >
                                        <i className="bi bi-trash" />
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {contracts.length === 0 && (
                    <NotFoundBox title={getText('msg_e1f222fb3cb6')} />
                )}
            </div>

            <span
                type="button"
                onClick={openAddModal}
                className="ing-text-buttons mt-3 d-inline-flex align-items-center ps-4"
            >
                <i
                    className={`bi bi-plus-circle-fill me-2 ${styles.addIconBadge}`}
                />
                {getText('msg_12a801b9778b')}
            </span>

            {isAddModalOpen && (
                <div
                    className="modal d-block"
                    tabIndex={-1}
                    style={{ background: 'rgba(0,0,0,0.5)' }}
                >
                    <div className="modal-dialog">
                        <div className="modal-content p-4">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="m-0">
                                    {getText('msg_12a801b9778b')}
                                </h5>
                                <span
                                    type="button"
                                    onClick={closeAddModal}
                                    className="ing-text-buttons"
                                >
                                    <i className="bi bi-x" />
                                </span>
                            </div>

                            <div className="form-group mb-3">
                                <label htmlFor="title">
                                    {getText('msg_768e0c1c6957')}{' '}
                                    <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="title"
                                    name="title"
                                    className="form-control"
                                    value={newContractForm.title}
                                    onChange={e =>
                                        setNewContractForm(prevData => ({
                                            ...prevData,
                                            title: e.target.value,
                                        }))
                                    }
                                />
                            </div>

                            <div className="form-group mb-3">
                                <label htmlFor="contractStatusId">
                                    {getText('msg_bae7d5be7082')}{' '}
                                    <span className="text-danger">*</span>
                                </label>
                                <Dropdown
                                    defaultValue={
                                        statusOptions.find(
                                            option =>
                                                option.value ===
                                                newContractForm.contractStatusId,
                                        )?.label || getText('msg_f409e9bea59c')
                                    }
                                    options={statusOptions}
                                    onChange={value =>
                                        setNewContractForm(prevData => ({
                                            ...prevData,
                                            contractStatusId: value,
                                        }))
                                    }
                                />
                            </div>

                            <div className="form-group mb-3">
                                <label htmlFor="startDate">
                                    {getText('msg_faa9e7e7ef5a')}
                                </label>
                                <CustomDatePicker
                                    selectedDate={newContractForm.startDate}
                                    onDateChange={date =>
                                        setNewContractForm(prevData => ({
                                            ...prevData,
                                            startDate: date,
                                        }))
                                    }
                                />
                            </div>

                            <div className="form-group mb-3">
                                <label htmlFor="completeDate">
                                    {getText('msg_1798b3ba42ee')}
                                </label>
                                <CustomDatePicker
                                    selectedDate={newContractForm.completeDate}
                                    onDateChange={date =>
                                        setNewContractForm(prevData => ({
                                            ...prevData,
                                            completeDate: date,
                                        }))
                                    }
                                />
                            </div>

                            <div className="form-group mb-3">
                                <label htmlFor="contractDocument">
                                    {getText('msg_e214b8a29923')}
                                </label>
                                <input
                                    type="file"
                                    id="contractDocument"
                                    name="contractDocument"
                                    className="form-control"
                                    onChange={handleContractFileChange}
                                    disabled={isUploadingDocument}
                                />
                                {isUploadingDocument && (
                                    <small className="text-muted">
                                        {getText('msg_87ebdbd3ea83')}
                                    </small>
                                )}
                                {!isUploadingDocument &&
                                    newContractForm.fileName && (
                                        <small className="text-muted">
                                            {newContractForm.fileName}
                                        </small>
                                    )}
                            </div>

                            <div className="text-end">
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleAddContract}
                                    disabled={isSaving || isUploadingDocument}
                                >
                                    {isSaving
                                        ? getText('msg_268c06a28a1b')
                                        : getText('msg_61cc55aa0453')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BillingContractsTab;
